import {Modal} from '@/components/atoms'
import {MODAL_TYPE} from '@/utils/constants/globalStyle'
import {BuyType} from '@/utils/constants/promotionConstants'
import {useMemo} from 'react'
import SelectionModalContent from '../SelectionModalContent'
import {useSelectionModalContent} from '../SelectionModalContent/selectionModalContent-hook'

/**
 * Modal component for selecting promotion items (categories, brands, products, etc.)
 *
 * @param {Object} props - Component props
 * @param {boolean} props.show - Controls the visibility of the modal
 * @param {string} props.type - Type of items to select from. Must be one of BuyType values:
 *   - BuyType.CATEGORY ('CATEGORIES') - For selecting categories/subcategories
 *   - BuyType.BRAND ('BRANDS') - For selecting brands/sub-brands
 *   - BuyType.PRODUCT ('PRODUCTS') - For selecting products
 *   - BuyType.PRODUCT_ID ('PRODUCT IDS') - For selecting by product IDs
 *   - BuyType.GTINS ('GTINs') - For selecting by GTIN codes
 * @param {Function} props.onClose - Callback function called when modal is closed/cancelled
 * @param {Function} props.onSubmit - Callback function called when Apply button is clicked.
 *   Receives two parameters: (selectedTags, combination)
 *   - selectedTags: Object with itemType and values array of selected items
 *   - combination: Boolean indicating if product combination is enabled
 * @param {Object} [props.conditionValues] - Initial values to populate the modal
 * @param {boolean} [props.conditionValues.productCombination] - Initial combination setting
 * @param {string} [props.conditionValues.itemType] - Initial item type
 * @param {Array} [props.conditionValues.values] - Initial selected values array
 * @param {boolean} [props.isExclude=false] - Reserved for future exclusion logic (currently unused)
 */
const PromotionSelectionModal = ({show, type, onClose, onSubmit, conditionValues, isExclude = false}) => {
  // Note: isExclude parameter is reserved for future exclusion logic implementation

  // Create a key that changes when conditionValues change, forcing component re-mount
  const modalKey = useMemo(() => {
    return `${type}-${conditionValues?.itemType || ''}-${conditionValues?.values?.length || 0}-${conditionValues?.productCombination || false}`
  }, [type, conditionValues])

  const selectionModalHookValue = useSelectionModalContent(type, conditionValues)
  const {combination, selectedTags, isPrimaryButtonDisabled} = selectionModalHookValue

  const getModalTitle = () => {
    switch (type) {
      case BuyType.CATEGORY:
        return 'Category/Sub Category'
      case BuyType.BRAND:
        return 'Brands / Sub Brands'
      case BuyType.PRODUCT:
        return 'Products'
      case BuyType.PRODUCT_ID:
        return 'Product ID List'
      case BuyType.GTINS:
        return 'GTIN List'
      default:
        return ''
    }
  }

  const handleSubmit = () => {
    if (onSubmit) {
      onSubmit(selectedTags, combination)
    }
  }

  const getModalDescription = () => {
    if (isExclude) {
      return 'What do you want to exclude from this deal?'
    }
    switch (type) {
      case BuyType.CATEGORY:
      case BuyType.BRAND:
      case BuyType.PRODUCT:
        return 'What do you want this deal to apply to?'
      case BuyType.PRODUCT_ID:
      case BuyType.GTINS:
      default:
        return ''
    }
  }

  return (
    <Modal
      key={modalKey}
      showModal={show}
      isDark
      title={getModalTitle()}
      text={getModalDescription()}
      primaryButtonText="Apply"
      tertiaryButtonText="Cancel"
      showTertiaryButton={true}
      classNameWrapper="min-h-80"
      content={
        <SelectionModalContent type={type} selectionModalHookValue={selectionModalHookValue} isExclude={isExclude} />
      }
      type={[BuyType.CATEGORY, BuyType.BRAND, BuyType.PRODUCT].includes(type) ? MODAL_TYPE.SMALL : MODAL_TYPE.FLUID}
      onClickTertiaryButton={onClose}
      onClickPrimaryButton={handleSubmit}
      hideCloseButton={true}
      primaryButtonDisabled={isPrimaryButtonDisabled}
    />
  )
}

export default PromotionSelectionModal
